import { useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useResourceFilters, ResourceFilters } from '@/hooks/useResources';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { SHARED_FILTER_OPTIONS } from '@/data/filters';

interface ResourceFilterSidebarProps {
  filters: ResourceFilters;
  onFiltersChange: (filters: ResourceFilters) => void;
  isOpen: boolean;
  onClose: () => void;
}

export function ResourceFilterSidebar({ 
  filters, 
  onFiltersChange, 
  isOpen, 
  onClose 
}: ResourceFilterSidebarProps) {
  const { data: filterOptions, isLoading } = useResourceFilters();

  const totalActiveFilters = 
    filters.contentTypes.length + 
    filters.topics.length + 
    filters.useCases.length;

  const clearAllFilters = () => {
    onFiltersChange({
      contentTypes: [],
      topics: [],
      useCases: []
    });
  };

  const toggleFilter = (type: keyof ResourceFilters, value: string) => {
    const currentValues = filters[type] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFiltersChange({
      ...filters,
      [type]: newValues
    });
  };

  interface FilterSectionProps {
    title: string;
    items: string[];
    type: keyof ResourceFilters;
    loading?: boolean;
    defaultOpen?: boolean;
  }

  function FilterSection({ title, items, type, loading, defaultOpen = true }: FilterSectionProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const selected = filters[type] as string[];

    return (
      <div className="border-b border-border pb-4">
        <Button
          variant="ghost"
          className="w-full justify-between p-0 h-auto font-medium text-text-primary hover:text-accent"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{title}</span>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
        
        {isOpen && (
          <div className="mt-3 space-y-2">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 flex-1" />
                </div>
              ))
            ) : (
              items.map((item) => (
                <div key={item} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${type}-${item}`}
                    checked={selected.includes(item)}
                    onCheckedChange={() => toggleFilter(type, item)}
                    className="data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                  />
                  <label
                    htmlFor={`${type}-${item}`}
                    className="text-sm text-text-secondary cursor-pointer hover:text-text-primary transition-colors duration-fast"
                  >
                    {item}
                  </label>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  const sidebarContent = (
    <div className="h-full overflow-y-auto scrollbar-thin p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Filters</h2>
          {totalActiveFilters > 0 && (
            <Badge variant="secondary" className="bg-accent-light text-accent">
              {totalActiveFilters}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {totalActiveFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-text-tertiary hover:text-error text-xs"
            >
              Clear all
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="lg:hidden text-text-tertiary hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filter sections */}
      <div className="space-y-6">
        <FilterSection
          title="Content Type"
          items={filterOptions?.contentTypes || []}
          type="contentTypes"
          loading={isLoading}
        />
        
        <FilterSection
          title="Topics"
          items={filterOptions?.topics || []}
          type="topics"
          loading={isLoading}
        />
        
        <FilterSection
          title="Use Cases"
          items={SHARED_FILTER_OPTIONS.useCases}
          type="useCases"
          loading={false}
        />
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={cn(
        "fixed top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-background border-r border-border transform transition-transform duration-base z-50 lg:sticky lg:transform-none lg:z-auto",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {sidebarContent}
      </aside>
    </>
  );
}